import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router";
import {
  Download,
  AlertTriangle,
  Info,
  Loader2,
  PenLine,
  ShieldOff,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { useIsAuthenticated } from "@/store/authStore";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { toast } from "sonner";
import { PublicQuoteTemplateModern } from "./templates/PublicQuoteTemplateModern";
import { PublicQuoteTemplateTwoColumnClassic } from "./templates/PublicQuoteTemplateTwoColumnClassic";
import { PublicQuoteTemplateProfessionalGrid } from "./templates/PublicQuoteTemplateProfessionalGrid";
import { PublicQuoteTemplateIndustrialQuotation } from "./templates/PublicQuoteTemplateIndustrialQuotation";
import { PublicQuoteTemplateSteelDistributor } from "./templates/PublicQuoteTemplateSteelDistributor";
import { PublicQuoteTemplateServiceQuote } from "./templates/PublicQuoteTemplateServiceQuote";
import { PublicQuoteTemplateFoundryQuote } from "./templates/PublicQuoteTemplateFoundryQuote";
import { PublicQuoteTemplateInjectionMolder } from "./templates/PublicQuoteTemplateInjectionMolder";
import { PublicQuoteTemplateErpQuotation } from "./templates/PublicQuoteTemplateErpQuotation";
import {
  PublicQuoteSignatureSection,
  canQuoteBeSigned,
} from "./PublicQuoteSignatureSection";
import type { PublicQuoteSignatureSectionHandle } from "./PublicQuoteSignatureSection";
import type { PublicQuote, QuoteTemplateId } from "@forgepoint/shared";
import {
  PublicQuotePdfResponseSchema,
  PublicQuoteResponseSchema,
} from "@forgepoint/shared";
import {
  DEFAULT_PUBLIC_QUOTE_TEMPLATE,
  isDateOnlyExpired,
} from "@forgepoint/shared";
import { PAGE_WIDTH_PX } from "./components/PdfPage";
import { getPublicQuoteBrandName } from "./utils";
import { PublicQuoteDocumentView } from "./components/PublicQuoteDocumentView";
import { PageWatermark } from "./components/PageWatermark";
import {
  clearBodyRenderComplete,
  markBodyRenderComplete,
} from "./components/renderReadySignal";

type TemplateComponent = typeof PublicQuoteTemplateModern;

const TEMPLATE_COMPONENTS: Record<QuoteTemplateId, TemplateComponent> = {
  modern: PublicQuoteTemplateModern,
  "two-column-classic": PublicQuoteTemplateTwoColumnClassic,
  "professional-grid": PublicQuoteTemplateProfessionalGrid,
  "industrial-quotation": PublicQuoteTemplateIndustrialQuotation,
  "steel-distributor": PublicQuoteTemplateSteelDistributor,
  "service-quote": PublicQuoteTemplateServiceQuote,
  "foundry-quote": PublicQuoteTemplateFoundryQuote,
  "injection-molder": PublicQuoteTemplateInjectionMolder,
  "erp-quotation": PublicQuoteTemplateErpQuotation,
};

export function PublicQuoteController() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const serverSideQuotePdfEnabled = useFeatureFlag("server-side-quote-pdf");
  const isAuthenticated = useIsAuthenticated();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const viewIdRef = useRef<string | null>(null);
  const endedRef = useRef(false);
  const signatureSectionRef = useRef<PublicQuoteSignatureSectionHandle>(null);
  const signatureSectionContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteId) {
        setError("No quote ID provided");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchApi(`public/quotes/${quoteId}`, "GET");
        if (response.ok) {
          const data = PublicQuoteResponseSchema.parse(response.data);
          setQuote(data.quote);
        } else {
          if (response.status === 410) {
            setError("The requested quote could not be found.");
            return;
          }
          throw new Error(
            (response.data as { error?: string })?.error ||
              "Failed to load quote",
          );
        }
      } catch (err) {
        captureException(err as Error);
        setError(
          "Failed to load the quote. It might not exist or is not publicly accessible.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId]);

  useEffect(() => {
    if (!quoteId) {
      return;
    }

    endedRef.current = false;
    viewIdRef.current = null;

    const startView = async () => {
      try {
        const response = await fetchApi(
          `public/quotes/${quoteId}/view`,
          "POST",
        );
        if (
          response.ok &&
          response.data &&
          typeof response.data === "object" &&
          "viewId" in (response.data as Record<string, unknown>)
        ) {
          viewIdRef.current = (response.data as { viewId: string }).viewId;
        }
      } catch (err) {
        captureException(err as Error);
      }
    };

    const endView = async () => {
      if (!viewIdRef.current || endedRef.current) {
        return;
      }
      endedRef.current = true;
      try {
        await fetchApi(`analytics/views/${viewIdRef.current}/end`, "PATCH");
      } catch (err) {
        captureException(err as Error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        endView().catch(() => {});
      }
    };

    const handleBeforeUnload = () => {
      endView().catch(() => {});
    };

    startView();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      endView().catch(() => {});
    };
  }, [quoteId]);

  useEffect(() => {
    if (quote?.name) {
      document.title = quote.name;
    }
  }, [quote?.name]);

  // Render-ready signal for the PDF pipeline. The HTML-document path stamps
  // document.body itself once pagination settles (via QuoteDocumentRenderer's
  // onSettled); the legacy template path renders synchronously, so we stamp the
  // body after the quote has painted. Cleared while loading/erroring so a
  // re-render re-arms the wait.
  const rendersDocument = Boolean(quote?.quoteDocument);
  useEffect(() => {
    if (!quote || rendersDocument) {
      clearBodyRenderComplete();
      return;
    }
    const frame = requestAnimationFrame(() => {
      markBodyRenderComplete();
    });
    return () => cancelAnimationFrame(frame);
  }, [quote, rendersDocument]);

  const handlePdfExport = async () => {
    if (!quoteId || !quote) {
      return;
    }

    if (!serverSideQuotePdfEnabled) {
      window.print();
      return;
    }

    try {
      setIsExportingPdf(true);
      const response = await fetchApi(`public/quotes/${quoteId}/pdf`, "POST");

      if (!response.ok) {
        toast.error("Failed to export quote PDF.");
        return;
      }

      const { assetUrl } = PublicQuotePdfResponseSchema.parse(response.data);
      const assetResponse = await fetch(assetUrl);
      if (!assetResponse.ok) {
        throw new Error("Failed to download generated quote PDF");
      }

      const downloadUrl = URL.createObjectURL(await assetResponse.blob());
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${quoteId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (exportError) {
      captureException(exportError as Error);
      toast.error("Failed to export quote PDF.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  /**
   * Scrolls to the signature section and opens the sign dialog.
   */
  const handleSignClick = () => {
    signatureSectionContainerRef.current?.scrollIntoView({
      behavior: "smooth",
    });
    signatureSectionRef.current?.openSignDialog();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full bg-gray-50">
        <div className="flex flex-col items-center p-8">
          <div className="h-8 w-8 border-4 border-t-blue-500 border-gray-200 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-700">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen w-full bg-gray-50 p-4">
        <div className="max-w-md text-center bg-white p-8 rounded-lg shadow-sm">
          <h1 className="text-2xl font-bold mb-4 text-gray-800">
            Quote Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            {error || "The requested quote could not be found."}
          </p>
          <a
            href="/"
            className="text-blue-600 hover:text-blue-800 transition-colors"
          >
            Return to homepage
          </a>
        </div>
      </div>
    );
  }

  const isExpired = isDateOnlyExpired(quote.expirationDate);

  const selectedTemplate =
    quote.publicQuoteTemplate || DEFAULT_PUBLIC_QUOTE_TEMPLATE;
  const TemplateComponent =
    TEMPLATE_COMPONENTS[selectedTemplate] || PublicQuoteTemplateModern;
  const publicQuoteBrandName = getPublicQuoteBrandName(quote);
  const closeIfOpenedByApp = window.opener ? () => window.close() : undefined;
  const isPaginatedTemplate =
    selectedTemplate === "steel-distributor" ||
    selectedTemplate === "service-quote" ||
    selectedTemplate === "foundry-quote" ||
    selectedTemplate === "injection-molder" ||
    selectedTemplate === "erp-quotation" ||
    selectedTemplate === "modern";

  return (
    <div className="min-h-screen w-full flex flex-col bg-gray-50 print:bg-white print:min-h-0">
      {/* Add print styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media print {
          html {
            background: white !important;
          }

          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            size: auto;
            /* Setting margin to 0 prevents Chrome from rendering its native
               page title header and URL footer in the printed output. */
            margin: 0;
          }
          .quote-print-content {
            padding: 0.75in 0.5in;
          }
        }
      `,
        }}
      />

      {/* Banner for revoked public access (visible to internal org users) */}
      {quote.publicAccessRevokedAt && (
        <div className="bg-orange-50 border-b border-orange-200 print:hidden">
          <div className="px-4 py-3 sm:px-6">
            <div className="flex items-center">
              <ShieldOff className="h-5 w-5 text-orange-600 mr-2" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-orange-800">
                  Public access has been revoked
                </h3>
                <p className="text-xs text-orange-700 mt-0.5">
                  External users cannot view this quote. You can see it because
                  you are a member of the organization.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner for non-finalized quotes */}
      {quote.status !== "FINALIZED" && (
        <div className="bg-yellow-50 border-b border-yellow-200 print:hidden">
          <div className="px-4 py-3 sm:px-6">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-800">
                  This quote is not yet finalized
                </h3>
                <p className="text-xs text-yellow-700 mt-0.5">
                  Pricing and terms are subject to change. Please contact your
                  sales representative for the most up-to-date information.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner for old revisions */}
      {!quote.isLatestRevision && quote.latestRevisionId && (
        <div className="bg-red-50 border-b border-red-200 print:hidden">
          <div className="px-4 py-3 sm:px-6">
            <div className="flex items-center">
              <Info className="h-5 w-5 text-red-600 mr-2" />
              <div className="flex-1">
                <p className="text-sm text-red-800">
                  You're viewing an old revision. Pricing and terms may have
                  changed.{" "}
                  <Link
                    to={`/public/quotes/${quote.latestRevisionId}`}
                    className="font-semibold underline hover:text-red-900"
                  >
                    View the latest
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 print:hidden">
        <div className="px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-4">
                {isAuthenticated && (
                  <BackButton
                    className="-ml-1 min-[1400px]:hidden"
                    onClick={closeIfOpenedByApp}
                  />
                )}
                {quote.organization?.logoUrl && (
                  <img
                    src={quote.organization.logoUrl}
                    alt={`${publicQuoteBrandName ?? quote.organization.name} logo`}
                    className="h-8 w-auto object-contain"
                  />
                )}
                {publicQuoteBrandName && (
                  <span className="text-lg font-bold text-gray-900">
                    {publicQuoteBrandName}
                  </span>
                )}
                <span className="text-md text-muted-foreground">
                  {quote.name || "Quote"}
                </span>
              </div>

              <p className="text-xs text-gray-500 mt-0.5 sm:hidden">
                Quote #{quote.quoteDisplayNumber} Rev {quote.revision}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="hidden sm:block text-sm text-gray-500">
                Quote #{quote.quoteDisplayNumber} Rev {quote.revision}
              </span>
              <Button
                onClick={handlePdfExport}
                disabled={isExportingPdf}
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 text-xs sm:text-sm px-3 py-1.5"
              >
                {isExportingPdf ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                <span>{isExportingPdf ? "Exporting…" : "Export PDF"}</span>
              </Button>
              {canQuoteBeSigned(quote) && (
                <Button
                  onClick={handleSignClick}
                  variant="default"
                  size="sm"
                  className="flex items-center gap-1.5 text-xs sm:text-sm px-3 py-1.5"
                >
                  <PenLine className="h-3.5 w-3.5" />
                  <span>Sign Quote</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      {quote.quoteDocument ? (
        <main className="flex-1 w-full bg-gray-200 print:bg-white print:py-0">
          <PublicQuoteDocumentView
            quote={quote}
            documentHtml={quote.quoteDocument}
          />
          <div
            ref={signatureSectionContainerRef}
            className="mx-auto pb-8 print:hidden"
            style={{ width: PAGE_WIDTH_PX }}
          >
            <PublicQuoteSignatureSection
              ref={signatureSectionRef}
              quote={quote}
              onSignatureCreated={(updatedQuote) => setQuote(updatedQuote)}
            />
          </div>
        </main>
      ) : isPaginatedTemplate ? (
        <main className="flex-1 w-full bg-gray-200 print:bg-white print:py-0">
          <TemplateComponent quote={quote} isExpired={Boolean(isExpired)} />
          <div
            ref={signatureSectionContainerRef}
            className="mx-auto pb-8 print:hidden"
            style={{ width: PAGE_WIDTH_PX }}
          >
            <PublicQuoteSignatureSection
              ref={signatureSectionRef}
              quote={quote}
              onSignatureCreated={(updatedQuote) => setQuote(updatedQuote)}
            />
          </div>
        </main>
      ) : (
        <main className="flex-1 w-full print:py-0">
          {quote.watermark && (
            <PageWatermark watermark={quote.watermark} fixed />
          )}
          <div className="sm:max-w-7xl sm:mx-auto sm:p-6 lg:p-8 print:max-w-none quote-print-content">
            <TemplateComponent quote={quote} isExpired={Boolean(isExpired)} />
            <div ref={signatureSectionContainerRef}>
              <PublicQuoteSignatureSection
                ref={signatureSectionRef}
                quote={quote}
                onSignatureCreated={(updatedQuote) => setQuote(updatedQuote)}
              />
            </div>
          </div>
        </main>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-3 sm:py-4 w-full print:hidden">
        <p className="text-center text-xs text-gray-500 px-4">
          Generated by Forgepoint
        </p>
      </footer>
    </div>
  );
}
